import { createAction, props } from '@ngrx/store';

// eslint-disable-next-line max-len
import { ICreateCustomInsightDialogData } from '@live/pages/live-ai-models-and-presets-page/tabs/live-custom-insights-tab/components/live-custom-insight-dialog/interfaces';
// eslint-disable-next-line max-len
import { ICreateCustomModelDialogData } from '@live/pages/live-ai-models-and-presets-page/tabs/live-custom-insights-tab/components/live-custom-model-dialog/interfaces';

import PaginatedResult = Microsoft.VideoIndexer.Contracts.PaginatedResult;
import ICustomInsight = Microsoft.VideoIndexer.Live.Contracts.ICustomInsight;
import IInsightImage = Microsoft.VideoIndexer.Live.Contracts.IInsightImage;
import SupportedModel = Microsoft.VideoIndexer.Live.Contracts.SupportedModel;

export const loadAllInsights = createAction('[Live Insights/API] Load all insights');

export const openCreateCustomInsightDialog = createAction('[Live custom insights] Open create custom insight dialog');
export const openEditCustomInsightDialog = createAction(
  '[Live custom insights] Open edit custom insight dialog',
  props<{ insight: ICustomInsight }>()
);
export const openDuplicateCustomInsightDialog = createAction(
  '[Live custom insights] Open duplicate custom insight dialog',
  props<{ insight: ICustomInsight }>()
);

// Actions for listing custom insights
export const listCustomInsights = createAction('[Live custom insights/API] List Custom Insights');
export const listCustomInsightsSuccess = createAction(
  '[Live custom insights/API] List Custom Insights Success',
  props<{ response: PaginatedResult<ICustomInsight> }>()
);
export const listCustomInsightsFailure = createAction('[Live custom insights/API] List Custom Insights Failure', props<{ error: string }>());

// Actions for creating custom insights
export const createCustomInsight = createAction(
  '[Live custom insights/API] Create Custom Insight',
  props<{ insightData: ICreateCustomInsightDialogData }>()
);
export const createCustomInsightSuccess = createAction(
  '[Live custom insights/API] Create Custom Insight Success',
  props<{ insight: ICustomInsight }>()
);
export const createCustomInsightFailure = createAction('[Live custom insights/API] Create Custom Insight Failure', props<{ error: string }>());

// Actions for updating custom insights
export const updateCustomInsight = createAction(
  '[Live custom insights/API] Update Custom Insight',
  props<{ insightId: string; insightData?: ICreateCustomInsightDialogData; imageId?: string; imageName?: string }>()
);
export const updateCustomInsightSuccess = createAction(
  '[Live custom insights/API] Update Custom Insight Success',
  props<{ insight: ICustomInsight }>()
);
export const updateCustomInsightFailure = createAction('[Live custom insights/API] Update Custom Insight Failure', props<{ error: string }>());

// Actions for deleting custom insights
export const openDeleteCustomInsightDialog = createAction(
  '[Live custom insights] Open Delete Custom Insight Dialog',
  props<{ insights: ICustomInsight[] }>()
);
export const deleteCustomInsights = createAction('[Live custom insights/API] Delete Custom Insights', props<{ insightIds: string[] }>());
export const deleteCustomInsight = createAction('[Live custom insights/API] Delete Custom Insight', props<{ insightId: string }>());
export const deleteCustomInsightSuccess = createAction('[Live custom insights/API] Delete Custom Insight Success', props<{ insightId: string }>());
export const deleteCustomInsightFailure = createAction(
  '[Live custom insights/API] Delete Custom Insight Failure',
  props<{ error: string; insightId: string }>()
);
export const deleteCustomInsightsBatchSuccess = createAction(
  '[Live custom insights/API] Delete Custom Insights Batch Success',
  props<{ insightIds: string[] }>()
);
export const deleteCustomInsightsBatchFailure = createAction(
  '[Live custom insights/API] Delete Custom Insights Batch Failure',
  props<{ error: string }>()
);

// Actions for uploading custom insight images
export const uploadCustomInsightImage = createAction(
  '[Live Custom Insights/API] Upload Custom Insight Image',
  props<{ insightId: string; formData: FormData; imageName: string }>()
);

export const uploadCustomInsightImageSuccess = createAction(
  '[Live Custom Insights/API] Upload Custom Insight Image Success',
  props<{ insightId: string; imageName: string; imageId: string }>()
);

export const uploadCustomInsightImageFailure = createAction(
  '[Live Custom Insights/API] Upload Custom Insight Image Failure',
  props<{ error: string }>()
);

// Actions for getting custom insight images
export const getCustomInsightImage = createAction(
  '[Live Custom Insights/API] Get Custom Insight Image',
  props<{ insightId: string; imageId: string }>()
);

export const getCustomInsightImageSuccess = createAction(
  '[Live Custom Insights/API] Get Custom Insight Image Success',
  props<{ insightId: string; imageId: string; imageData: string }>()
);

export const getCustomInsightImageFailure = createAction('[Live Custom Insights/API] Get Custom Insight Image Failure', props<{ error: string }>());

// Actions for deleting custom insight images
export const deleteCustomInsightImage = createAction(
  '[Live Custom Insights/API] Delete Custom Insight Image',
  props<{ insightId: string; imageId: string }>()
);

export const deleteCustomInsightImageSuccess = createAction(
  '[Live Custom Insights/API] Delete Custom Insight Image Success',
  props<{ insightId: string; imageId: string }>()
);

export const deleteCustomInsightImageFailure = createAction(
  '[Live Custom Insights/API] Delete Custom Insight Image Failure',
  props<{ error: string }>()
);

// Actions for temporary image file management
export const setTemporaryImageFile = createAction('[Live Custom Insights] Set Temporary Image File', props<{ file: File }>());

export const clearTemporaryImageFile = createAction('[Live Custom Insights] Clear Temporary Image File');

// Action for handling image change during insight editing
export const handleInsightImageChange = createAction(
  '[Live Custom Insights] Handle Insight Image Change',
  props<{
    insightId: string;
    oldImageId?: string;
    newImage?: File;
    insightData: ICreateCustomInsightDialogData;
  }>()
);

// Actions for sequential insight type switching (text to image)
export const switchInsightTypeToImage = createAction(
  '[Live custom insights/API] Switch Insight Type To Image',
  props<{
    insightId: string;
    newImage: File;
    insightData: ICreateCustomInsightDialogData;
  }>()
);

export const switchInsightTypeToImageSuccess = createAction(
  '[Live custom insights] Switch Insight Type To Image Success',
  props<{ insight: ICustomInsight }>()
);

export const switchInsightTypeToImageFailure = createAction(
  '[Live custom insights] Switch Insight Type To Image Failure',
  props<{ error: string }>()
);

export const openCreateCustomModelDialog = createAction('[Live custom models] Open create custom model dialog');

export const openEditCustomModelDialog = createAction('[Live custom models] Open edit custom model dialog', props<{ model: ICustomInsight }>());

export const openDuplicateCustomModelDialog = createAction(
  '[Live custom models] Open Duplicate Custom Model Dialog',
  props<{ model: ICustomInsight }>()
);

export const createCustomModel = createAction(
  '[Live custom models/API] Create Custom Model',
  props<{
    modelData: ICreateCustomModelDialogData;
    formData?: FormData;
  }>()
);

export const createCustomModelSuccess = createAction(
  '[Live custom models/API] Create Custom Model Success',
  props<{
    model: ICustomInsight;
    uploadCounts?: {
      positive: { uploadsAttempted: number; uploadsSucceeded: number; uploadsFailed: number };
      negative: { uploadsAttempted: number; uploadsSucceeded: number; uploadsFailed: number };
    };
  }>()
);

export const createCustomModelFailure = createAction('[Live custom models/API] Create Custom Model Failure', props<{ error: string }>());

export const updateCustomModel = createAction(
  '[Live custom models/API] Update Custom Model',
  props<{
    modelId: string;
    modelData: ICreateCustomModelDialogData;
    formData?: FormData;
  }>()
);

export const updateCustomModelSuccess = createAction('[Live custom models/API] Update Custom Model Success', props<{ model: ICustomInsight }>());

export const updateCustomModelFailure = createAction(
  '[Live custom models/API] Update Custom Model Failure',
  props<{
    error: string;
    modelId: string;
    telemetryContext: {
      insightId: string;
      insightName: string;
      modelType: SupportedModel;
      hasPositiveText: boolean;
      hasNegativeText: boolean;
      hasPositiveImages: boolean;
      hasNegativeImages: boolean;
      deleteCounts?: {
        positive: { attempted: number; deleted: number; failed: number };
        negative: { attempted: number; deleted: number; failed: number };
      };
    };
  }>()
);

export const uploadCustomModelImages = createAction(
  '[Live custom models/API] Upload Custom Model Images',
  props<{
    modelId: string;
    formData: FormData;
  }>()
);

export const uploadCustomModelImagesComplete = createAction(
  '[Live custom models/API] Upload Custom Model Images Complete',
  props<{ modelId: string; successfulImages: IInsightImage[]; failedImages: IInsightImage[] }>()
);

export const deleteCustomModelImages = createAction(
  '[Live custom models/API] Delete Custom Model Images',
  props<{
    modelId: string;
    imageIds: string[];
    updatedModel: ICustomInsight;
    modelData: ICreateCustomModelDialogData;
    formData?: FormData;
  }>()
);

export const deleteCustomModelImagesComplete = createAction(
  '[Live custom models/API] Delete Custom Model Images Complete',
  props<{
    modelId: string;
    deletedIds: string[];
    failedIds: string[];
    updatedModel: ICustomInsight;
    modelData: ICreateCustomModelDialogData;
    formData?: FormData;
  }>()
);

export const uploadCustomModelImagesStarted = createAction(
  '[Live custom models/API] Upload Custom Model Images Started',
  props<{
    modelId: string;
    formData: FormData;
    context: {
      updatedModel: ICustomInsight;
      modelData: ICreateCustomModelDialogData;
      deleteCounts: { deletedPositive: number; deletedNegative: number } | null;
    };
  }>()
);

export const uploadCustomModelImagesFinished = createAction(
  '[Live custom models/API] Upload Custom Model Images Finished',
  props<{
    context: {
      updatedModel: ICustomInsight;
      modelData: ICreateCustomModelDialogData;
      deleteCounts?: { deletedPositive: number; deletedNegative: number };
    };
    uploadResult: {
      successfulImages: IInsightImage[];
      failedImages: IInsightImage[];
    };
  }>()
);
